<template>
  <div>
    <!-- Only one element for Transition -->
    <div class="row">
      <div class="col-12">
        <SelectInputComponent
          v-model="companionsCount"
          :label="`Nombre d'${GetCompanionLabel(participation?.travel.companionLabel, 2)}`"
          :placeholder="`Nombre d'${GetCompanionLabel(participation?.travel.companionLabel, 2)}`"
          :options="Array.from({ length: 7 }, (_, i) => ({ value: i, label: i.toString(), selected: companionsCount === i }))"
          :required="true"
          class="mb-3 col-12 col-md-6 mx-auto"
        />
        <div class="text-center" v-if="participation?.travel.invoiceEnabled">
          <small class="text-muted">
            <i class="fa-solid fa-triangle-exclamation text-warning me-1"></i>
            Les {{ GetCompanionLabel(participation?.travel.companionLabel, 2) }} ajoutés ici figureront sur une même facture.<br>
<em>( Pour une facturation séparée en chambre Twin, indiquez 0 {{ GetCompanionLabel(participation?.travel.companionLabel) }} : l'option vous sera proposée lors du choix de l'hébergement )</em>
          </small>
        </div>
      </div>
    </div>

    <template v-if="companionsFields.length > 0">
      <div v-for="(companion, index) in companionsFields" :key="companion.key" class="card mt-3">
        <div class="card-header d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">
            <i class="fa-solid fa-user me-2"></i>
            {{ GetCompanionLabel(participation?.travel.companionLabel, 1, true) }} {{ index + 1 }}
          </h5>
        </div>

        <div class="card-body">
          <!-- Civilité et Statut -->
          <div class="row">
            <div class="col-12 text-center">
              <CheckboxButtonComponent
                :value-key="`companions[${index}].relation`"
                :required="true"
                :options="Object.values(participation?.travel.type === TravelDbTypeEnum.Private ? UserRelationEnumPrivate : UserRelationEnumOther).map(type => ({ value: type as UserRelationEnum, label: participation?.travel.type === TravelDbTypeEnum.Private ? GetRelationLabelPrivate(type as UserRelationEnum) : GetRelationLabel(type as UserRelationEnum) }))"
                class="mb-3"
              />
            </div>
            <div class="col-12 col-sm-6">
              <SelectInputComponent
                :value-key="`companions[${index}].civility`"
                label="Civilité"
                placeholder="Sélectionnez une civilité"
                :required="true"
                class="mb-3"
                :options="Object.values(CivilityEnum).map(type => ({ value: type, label: GetCivilityShortLabel(type) }))"
              />
            </div>
            <div class="col-12 col-sm-6" v-if="participation?.travel?.userStatusRequired">
              <SelectInputComponent
                :value-key="`companions[${index}].status`"
                :label="`Mon ${GetCompanionLabel(participation?.travel.companionLabel)}` + ' est'"
                placeholder="Sélectionnez un statut"
                :required="true"
                class="mb-3"
                :options="Object.values(participation?.travel?.usersStatusAvailable || UserStatusEnum).map(type => ({ value: type, label: GetUserStatusLabel(type) }))"
              />
            </div>
          </div>

          <!-- Nom et Prénom -->
          <div class="row">
            <div class="col-12 col-sm-6">
              <TextInputComponent
                :value-key="`companions[${index}].lastName`"
                label="Nom"
                placeholder="Nom"
                :required="true"
                class="mb-3"
                :upper-case="true"
              />
            </div>
            <div class="col-12 col-sm-6">
              <TextInputComponent
                :value-key="`companions[${index}].firstName`"
                label="Prénom"
                placeholder="Prénom"
                :required="true"
                class="mb-3"
                :capitalize="true"
              />
            </div>
          </div>

          <!-- Email -->
          <div class="row">
            <div class="col-12 col-md-6">
              <TextInputComponent
                :value-key="`companions[${index}].email`"
                label="Email"
                placeholder="Email"
                :required="false"
                class="mb-3"
                type="email"
              />
            </div>
            <div class="col-12 col-md-6">
              <PhoneInputComponent
                :value-key="`companions[${index}].mobilePhone`"
                label="Téléphone"
                placeholder="Téléphone"
                class="mb-3"
                :required="false"
              />
            </div>
          </div>
          <hr />
          <div class="row mt-md-4">
            <div class="col-12 col-md-6" v-if="participation?.travel?.nationalityRequired">
              <SelectInputComponent
                :value-key="`companions[${index}].nationality`"
                label="Nationalité"
                placeholder="Sélectionnez une nationalité"
                :required="true"
                class="mb-3"
                :options="Nationalities"
              />
            </div>
            <div class="col-12 col-md-6" v-if="participation?.travel?.birthDateRequired">
              <DatePickerComponent
                :value-key="`companions[${index}].birthDate`"
                label="Date de naissance"
                placeholder="Date de naissance"
                :required="true"
                class="mb-3"
                :max-date="new Date()"
              />
            </div>
          </div>

          <!-- Passeport -->
          <template v-if="participation?.travel?.passeportRequired">
            <hr />
            <div class="row mt-md-4">
              <div class="col-12 col-md-6">
                <TextInputComponent
                  :value-key="`companions[${index}].passportNumber`"
                  label="Numéro de passeport"
                  placeholder="Numéro de passeport"
                  :required="true"
                  class="mb-3"
                  :upper-case="true"
                />
              </div>
              <div class="col-12 col-md-6">
                <DatePickerComponent
                  :value-key="`companions[${index}].passportExpirationDate`"
                  label="Date d'expiration du passeport"
                  placeholder="Date d'expiration du passeport"
                  :required="true"
                  class="mb-3"
                  :min-date="passeportMinDate"
                />
              </div>
              <div class="col-12">
                <i class="fa-solid fa-triangle-exclamation text-warning fa-lg px-2"></i>
                L'{{ GetCompanionLabel(participation?.travel.companionLabel) }} doit être en possession d'un passeport valide jusqu'à <span v-html="participation?.travel?.passeportSixMonthsRequired == true ? '6 mois après la date de retour, soit le ' + formatDate(passeportMinDate,'DD MMM YYYY') : 'la date de retour'"></span>.
              </div>
            </div>
          </template>

          <!-- ESTA -->
          <template v-if="participation?.travel?.estaRequired">
            <hr v-if="!participation?.travel?.passeportRequired" />
            <div class="row mt-md-4">
              <div class="col-12 col-md-6">
                <TextInputComponent
                  :value-key="`companions[${index}].estaNumber`"
                  label="Numéro ESTA"
                  placeholder="Numéro ESTA"
                  :required="true"
                  name="esta-authorization"
                  autocomplete="one-time-code"
                  class="mb-3"
                />
              </div>
              <div class="col-12">
                <i class="fa-solid fa-triangle-exclamation text-warning fa-lg px-2"></i>
                L'{{ GetCompanionLabel(participation?.travel.companionLabel) }} doit être en possession d'un ESTA valide pour cet événement.
              </div>
            </div>
          </template>

          <template v-if="companion.value.infoFields && companion.value.infoFields.length > 0">
            <hr />
            <div class="row">
              <template v-for="(infoField, fieldIdx) in companion.value.infoFields" :key="infoField.id">
                <!-- Champ texte ou textarea -->
                <div
                  :class="[infoField.type == TravelDbFieldTypeEnum.TextArea ? 'col-12' : 'col-12 col-sm-6']"
                  v-if="[TravelDbFieldTypeEnum.Text, TravelDbFieldTypeEnum.TextArea].includes(infoField.type)"
                >
                  <TextInputComponent
                    :value-key="`companions[${index}].infoFields[${fieldIdx}].value`"
                    :label="infoField.label"
                    :placeholder="infoField.label"
                    :required="infoField.required"
                    class="mb-3"
                    :type="infoField.type == TravelDbFieldTypeEnum.TextArea ? 'multiline' : 'text'"
                  />
                </div>

                <!-- Champ date -->
                <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Date].includes(infoField.type)">
                  <DatePickerComponent
                    :value-key="`companions[${index}].infoFields[${fieldIdx}].value`"
                    :label="infoField.label"
                    :placeholder="infoField.label"
                    :required="infoField.required"
                    class="mb-3"
                  />
                </div>

                <!-- Champ numérique -->
                <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Number].includes(infoField.type)">
                  <NumberInputComponent
                    :value-key="`companions[${index}].infoFields[${fieldIdx}].value`"
                    :value-as-string="true"
                    :label="infoField.label"
                    :placeholder="infoField.label"
                    :required="infoField.required"
                    class="mb-3"
                  />
                </div>

                <!-- Champ booléen -->
                <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Boolean].includes(infoField.type)">
                  <ToggleCheckboxComponent
                    :value-key="`companions[${index}].infoFields[${fieldIdx}].value`"
                    :inverseOrder="true"
                    labelTrue="Oui"
                    labelFalse="Non"
                    :label="infoField.label"
                    :title="infoField.label"
                    :required="infoField.required"
                    class="mb-3"
                  />
                </div>

                <!-- Champ de choix -->
                <div class="col-12 col-sm-6" v-if="[TravelDbFieldTypeEnum.Choice].includes(infoField.type)">
                  <SelectInputComponent
                    :value-key="`companions[${index}].infoFields[${fieldIdx}].value`"
                    :label="infoField.label"
                    :placeholder="infoField.label"
                    :required="infoField.required"
                    :emptyOptionLabel="'Sélectionnez une option'"
                    :options="infoField.choices?.map(option => ({ value: option, label: option })) ?? []"
                    class="mb-3"
                  />
                </div>
              </template>
            </div>
          </template>
        </div>
      </div>
    </template>

    <PageBottomMessage message-key="pageCompanions"></PageBottomMessage>
  </div>
</template>

<script lang="ts" setup>
import { ref,computed,onMounted } from 'vue';
import { useFieldArray } from 'vee-validate';
import { GetCompanionLabel, GetCivilityShortLabel, GetUserStatusLabel, GetRelationLabel, GetRelationLabelPrivate, formatDate } from '@/compositions/helper';
import { Nationalities } from '@/compositions/nationalities';

const participation = defineModel<UserParticipation>();
const companionsCountRef = ref<number>(0);

const companionsCount = computed<number>({
  get: () => companionsCountRef.value,
  set: (value: number) => {
    updateCompanionsCount(value);
    companionsCountRef.value = value;
  }
});

const passeportMinDate = computed(() => {
  if(participation.value?.travel?.passeportSixMonthsRequired) {
    const endDate = participation.value.travel.endDate ? new Date(participation.value.travel.endDate) : new Date();
    endDate.setMonth(endDate.getMonth() + 6);
    endDate.setDate(endDate.getDate() + 1);
    return endDate;
  }
  const result = participation.value?.travel?.endDate ? new Date(participation.value.travel.endDate) : new Date();
  result.setDate(result.getDate() + 1);
  return result;
});

const { remove: removeCompanion, push: pushCompanion, fields: companionsFields } = useFieldArray<UserParticipationCompanion>(() => "companions");

// Initialiser les champs info pour un accompagnant
const initializeInfoFields = (): UserParticipationInfoField[] => {
  if (!participation.value?.travel?.infoFields) return [];

  return participation.value.travel.infoFields.filter(x=>x.alsoForCompanion).map(field => ({
    id: field.id,
    rank: field.rank,
    type: field.type,
    label: field.label,
    required: field.required,
    choices: field.choices,
    value: null
  }));
};

// Créer un nouvel accompagnant
const createNewCompanion = (): UserParticipationCompanion => ({
  civility: undefined,
  status: undefined,
  lastName: undefined,
  firstName: undefined,
  email: undefined,
  mobilePhone: undefined,
  birthDate: undefined,
  nationality: undefined,
  passportNumber: undefined,
  passportExpirationDate: undefined,
  infoFields: initializeInfoFields()
});

// Mettre à jour le nombre d'accompagnants
const updateCompanionsCount = (value: number | undefined) => {
  const count = Math.max(Math.min(Number(value) || 0, 10), 0);

  if (count > companionsFields.value.length) {
    // Ajouter des accompagnants
    const toAdd = count - companionsFields.value.length;
    for (let i = 0; i < toAdd; i++) {
      pushCompanion(createNewCompanion());
    }
  } else if (count < companionsFields.value.length) {
    // Supprimer des accompagnants
    const toRemove = companionsFields.value.length - count;
    for (let i = 0; i < toRemove; i++) {
      removeCompanion(companionsFields.value.length - 1);
    }
  }
};

// Initialiser au montage
onMounted(() => {
  if (companionsFields.value) {
    companionsCount.value = companionsFields.value.length;
  }
});

// // Surveiller les changements pour synchroniser avec le modèle parent si nécessaire
// watch(companions, () => {
//   // Ici, vous pouvez synchroniser avec le modèle de participation
//   // par exemple: participation.value.companions = companions.value;
// }, { deep: true });
</script>
